// Tracked-issue reads for the repo Issues tab (arc 101 slice 06) — the first
// dashboard consumer of `GET /v1/repos/:owner/:repo/issues`
// (`apps/api/src/routes/issues.ts`). Speaks the shared list grammar
// (`filter=state:…`/`qualification:…`, default `sort=-github_number`, opaque
// `cursor`), same shape as `useRepoSessions`.
//
// The projection carries triage STATE plus the cached GitHub title (#980 —
// the DB always held it for pg_trgm dedup; the projection finally emits it).
// `title` is `''` on rows cached before the title backfill, so renderers fall
// back to the bare `#<n>`. The number links out to GitHub, where the prose
// lives; `last_session_id` links in to the session that decided it.

import type { IssueWire } from '@developerz/domain';
import { IssueWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { pageSchema } from './paged-schema';
import { repoApiBase, safeDecode } from './repo-detail';
import { SESSION_TOKEN } from './session';

/**
 * `/v1/repos/:owner/:repo/issues` row — INFERRED from `IssueResource`
 * (`packages/domain/src/wire/issue.ts`), the one declaration both this door and
 * the MCP `issue` verb serialize through.
 *
 * `title` is the ONE field restated, and it is restated WIDER on purpose: the
 * resource makes it required, this makes it optional so a browser holding a
 * newer bundle than the API it is talking to still type-checks the fallback
 * path. `''` on pre-cache rows (#980) and `undefined` mid-deploy render the
 * same `#<n>`, so the tolerance costs nothing at the call sites. Every other
 * field derives, and `Readonly` says what the schema cannot: this app never
 * mutates a response.
 */
export type RepoIssueRow = Readonly<Omit<IssueWire, 'title'>> & {
  readonly title?: string;
};

/** One page of tracked issues — the canonical `{ data, next_cursor }` shape. */
export type RepoIssuesPage = Page<RepoIssueRow>;

/**
 * The tab's body, PARSED (plan 101 slice 15 step 3), through the RESOURCE's own
 * schema rather than the widened type above.
 *
 * The widening is a type-level tolerance for a browser holding a newer bundle
 * than the API it is talking to; it is not a claim about what a CURRENT server
 * sends, and writing it into the schema would make the parse agree that a
 * titleless row is fine forever. Parsing narrow and typing wide is the honest
 * pair: today's door must send `title`, and the fallback path still compiles.
 */
const repoIssuesSchema = pageSchema(IssueWireSchema);

/** Issues-tab filter state; `''` on either axis means "any". */
export type IssueFilters = { readonly state: string; readonly qualification: string };

/** Filter axes the API's `ISSUE_LIST_SPEC` declares filterable — no dead knobs. */
export const ISSUE_STATES = ['', 'open', 'closed'] as const;
export const ISSUE_QUALIFICATIONS = ['', 'bug', 'feature', 'dupe', 'question', 'unknown'] as const;

export const ISSUES_QUERY_KEY = 'repo-issues';

/**
 * One page of a repo's tracked issues. `filters`/`cursor` are reactive so a
 * filter change or a load-more re-runs the query; `''` on a filter axis omits
 * that term entirely (any).
 */
export function useRepoIssues(
  encoded: () => string,
  filters: () => IssueFilters,
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<RepoIssuesPage>(() => ({
    queryKey: [
      ISSUES_QUERY_KEY,
      encoded(),
      filters().state,
      filters().qualification,
      cursor(),
      token,
      activeAccountId(),
    ],
    enabled: token !== null,
    queryFn: () =>
      apiFetch(
        `${repoApiBase(encoded())}/issues?${issueListParams(filters(), cursor())}`,
        token,
        undefined,
        { schema: repoIssuesSchema },
      ),
  }));
}

/** Build the `per_page`/`filter`/`cursor` query string, omitting empty filters. */
export function issueListParams(f: IssueFilters, cursor: string | null): string {
  const params = new URLSearchParams({ per_page: '20' });
  if (f.state) params.append('filter', `state:${f.state}`);
  if (f.qualification) params.append('filter', `qualification:${f.qualification}`);
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

/**
 * Label for a server enum (`issues_state` / `issues_qual` / `issues_handoff`),
 * defensively: a value the catalog has no key for renders verbatim rather than
 * as a loud missing-key marker. Shared by every surface that paints triage
 * state, so the row and the detail drawer can't disagree about a word.
 */
export function issueLabel(prefix: string, value: string): string {
  const key = `repo_detail.${prefix}_${value}`;
  return t.has(key) ? t(key) : value;
}

/**
 * Deep link to the issue on GitHub. The dashboard mirrors triage state, never
 * the issue body — the prose stays one click away at the source of truth.
 */
export function issueGithubUrl(encodedFullName: string, githubNumber: number): string {
  const [owner = '', ...rest] = safeDecode(encodedFullName).split('/');
  const repo = rest.join('/');
  return `https://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${githubNumber}`;
}
